import { useState } from 'react';
import { Team, TossChoice } from '@/types';
import { startScheduledMatch } from '@/lib/supabase';

interface StartMatchTossModalProps {
  matchId: string;
  matchNumber: number;
  teamA: Team;
  teamB: Team;
  onClose: () => void;
  onStarted: (matchData: any) => void;
}

export default function StartMatchTossModal({ matchId, matchNumber, teamA, teamB, onClose, onStarted }: StartMatchTossModalProps) {
  const [tossWonById, setTossWonById] = useState('');
  const [tossChoice, setTossChoice] = useState<TossChoice | ''>('');
  const [serveRotationCooloff, setServeRotationCooloff] = useState(5);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tossWinnerTeam = tossWonById === teamA.id ? teamA : tossWonById === teamB.id ? teamB : null;
  const otherTeam = tossWonById === teamA.id ? teamB : tossWonById === teamB.id ? teamA : null;

  const servingTeam = tossWinnerTeam && tossChoice === 'serve'
    ? tossWinnerTeam
    : tossWinnerTeam && tossChoice === 'side'
    ? otherTeam
    : null;

  const handleStart = async () => {
    if (!tossWonById) { setError('Select toss winner'); return; }
    if (!tossChoice) { setError('Select toss choice'); return; }

    setSaving(true);
    setError(null);
    try {
      const matchData = await startScheduledMatch(matchId, tossWonById, tossChoice, serveRotationCooloff);
      onStarted(matchData);
    } catch (err: any) {
      setError(err?.message || 'Failed to start match. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
      <div className="w-full max-w-md mx-auto bg-surface border-t border-border rounded-t-2xl p-4 pb-safe animate-slide-up">
        <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
        <h2 className="text-lg font-bold text-text-primary text-center mb-1">START MATCH #{matchNumber}</h2>
        <p className="text-xs text-text-muted text-center mb-5">Select toss result to begin scoring</p>

        {/* Teams reminder */}
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="font-bold text-base" style={{ color: teamA.color }}>{teamA.shortName}</span>
          <span className="text-text-muted text-sm">vs</span>
          <span className="font-bold text-base" style={{ color: teamB.color }}>{teamB.shortName}</span>
        </div>

        <div className="space-y-4 mb-5">
          {/* Toss Winner */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">Toss Won By</label>
            <div className="grid grid-cols-2 gap-2">
              {[teamA, teamB].map(team => (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => { setTossWonById(team.id); setTossChoice(''); }}
                  className={`py-3 px-3 rounded-lg font-bold text-sm border transition-all flex items-center gap-2 justify-center ${
                    tossWonById === team.id
                      ? 'border-2 border-current bg-opacity-20'
                      : 'bg-surface-raised border-border text-text-secondary hover:border-text-muted'
                  }`}
                  style={tossWonById === team.id ? { color: team.color, backgroundColor: `${team.color}20` } : {}}
                >
                  {team.logoUrl ? (
                    <img src={team.logoUrl} alt={team.shortName} className="w-5 h-5 rounded object-contain" />
                  ) : (
                    <span className="w-4 h-4 rounded-full" style={{ backgroundColor: team.color }} />
                  )}
                  {team.shortName}
                </button>
              ))}
            </div>
          </div>

          {/* Toss Choice */}
          {tossWonById && (
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                {tossWinnerTeam?.shortName} Chooses To
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(['serve', 'side'] as TossChoice[]).map(choice => (
                  <button
                    key={choice}
                    type="button"
                    onClick={() => setTossChoice(choice)}
                    className={`py-3 rounded-lg font-bold text-sm uppercase border transition-all ${
                      tossChoice === choice
                        ? 'bg-serving/20 border-serving text-serving'
                        : 'bg-surface-raised border-border text-text-secondary hover:border-text-muted'
                    }`}
                  >
                    {choice}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Serves First Preview */}
          {servingTeam && (
            <div className="bg-surface-raised border border-border rounded-lg p-3 text-center">
              <p className="text-xs text-text-muted mb-1">SERVES FIRST</p>
              <p className="font-bold text-sm" style={{ color: servingTeam.color }}>{servingTeam.name}</p>
            </div>
          )}

          {/* Serve Rotation Cooloff */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">Serve Rotation Cooloff</label>
            <p className="text-xs text-text-muted mb-2">Others who must serve before the same player can serve again (1–5)</p>
            <div className="flex items-center gap-3">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setServeRotationCooloff(n)}
                  className={`flex-1 py-2.5 rounded-lg font-bold text-sm border transition-all ${
                    serveRotationCooloff === n
                      ? 'bg-info/20 border-info text-info'
                      : 'bg-surface-raised border-border text-text-secondary'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="text-xs text-text-muted mt-1.5 text-center">
              {serveRotationCooloff === 5 ? '6-player (standard)' : serveRotationCooloff === 4 ? '5-player team' : `Custom`}
            </p>
          </div>
        </div>

        {error && <p className="text-error text-sm mb-3">{error}</p>}

        <div className="space-y-2">
          <button
            onClick={handleStart}
            disabled={saving || !tossWonById || !tossChoice}
            className="btn-primary bg-success text-white font-bold disabled:opacity-50"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                STARTING...
              </span>
            ) : 'START MATCH'}
          </button>
          <button onClick={onClose} disabled={saving} className="btn-secondary text-text-secondary">
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
